/**
 * Album Store
 *
 * Zustand store for managing albums and photos.
 * Hydrated from Supabase via `load()` (no persistence).
 *
 * @module features/albums/stores/albumStore
 */

import { create } from 'zustand';
import { toast } from '@/shared/hooks/useToast';
import { isPersistedId } from '@/shared/lib/persistedId';
import { registerFamilyScopedReset } from '@/shared/lib/storeReset';
import { ToastVariant } from '@/shared/types/toast.types';
import { albumService } from '../services/albumService';
import type {
  Album,
  Photo,
  PhotoComment,
  AlbumFilters,
  AlbumSortBy,
  SortDirection,
  CreateAlbumInput,
  UpdateAlbumInput,
  AddPhotoInput,
  UpdatePhotoInput,
} from '../types/album.types';
import {
  ALBUM_CATEGORY_CONFIG,
  DEFAULT_ALBUM_SETTINGS,
  DEFAULT_PHOTO_SETTINGS,
} from '../types/album.types';

// ============================================
// Types
// ============================================

interface AlbumState {
  // Data
  readonly albums: Album[];
  readonly photos: Photo[];
  readonly comments: PhotoComment[];
  readonly selectedAlbumId: string | null;
  readonly selectedPhotoId: string | null;

  // UI State
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly isLoaded: boolean;
  readonly filters: AlbumFilters;
  readonly sortBy: AlbumSortBy;
  readonly sortDirection: SortDirection;

  // View Mode
  readonly viewMode: 'grid' | 'list';
  readonly isViewerOpen: boolean;

  // Modals
  readonly isCreateModalOpen: boolean;
  readonly editingAlbumId: string | null;

  // Album Actions
  addAlbum: (input: CreateAlbumInput, createdBy: string) => Album;
  updateAlbum: (albumId: string, updates: Partial<UpdateAlbumInput>) => void;
  deleteAlbum: (albumId: string) => void;
  archiveAlbum: (albumId: string) => void;
  restoreAlbum: (albumId: string) => void;

  // Photo Actions
  addPhoto: (input: AddPhotoInput, uploadedBy: string) => Photo;
  updatePhoto: (photoId: string, updates: Partial<UpdatePhotoInput>) => void;
  deletePhoto: (photoId: string) => void;
  setAlbumCover: (albumId: string, photoId: string) => void;

  // Comment Actions
  addComment: (photoId: string, text: string, authorId: string) => PhotoComment;
  deleteComment: (commentId: string) => void;

  // Realtime Actions (P7)
  /**
   * Merge an album received from a realtime stream. Local-only — does NOT call
   * the service. Inserts when unseen, replaces in place when already present
   * (dedup by id, dropping stale echoes via `updatedAt`). Preserves the
   * locally-tracked `photoCount`, which is maintained by photo upserts.
   */
  upsertRealtimeAlbum: (album: Album) => void;
  /** Remove an album deleted on another device (cascades photos/comments). */
  deleteRealtimeAlbum: (albumId: string) => void;
  /**
   * Merge a photo received from a realtime stream. Local-only. Inserts when
   * unseen (and bumps the parent album's `photoCount`), replaces in place when
   * already present (dedup by `updatedAt`).
   */
  upsertRealtimePhoto: (photo: Photo) => void;
  /** Remove a photo deleted on another device (decrements `photoCount`). */
  deleteRealtimePhoto: (photoId: string) => void;
  /** Merge a photo comment received from a realtime stream. Local-only. */
  upsertRealtimeComment: (comment: PhotoComment) => void;
  /** Remove a photo comment deleted on another device. Local-only. */
  deleteRealtimeComment: (commentId: string) => void;

  // Selection Actions
  setSelectedAlbumId: (albumId: string | null) => void;
  setSelectedPhotoId: (photoId: string | null) => void;

  // Filter & Sort Actions
  setFilters: (filters: Partial<AlbumFilters>) => void;
  clearFilters: () => void;
  setSortBy: (sortBy: AlbumSortBy) => void;
  setSortDirection: (direction: SortDirection) => void;

  // View Actions
  setViewMode: (mode: 'grid' | 'list') => void;
  openViewer: (photoId: string) => void;
  closeViewer: () => void;

  // Modal Actions
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (albumId: string) => void;

  // Getters
  getAlbumById: (albumId: string) => Album | undefined;
  getPhotosByAlbumId: (albumId: string) => Photo[];
  getCommentsByPhotoId: (photoId: string) => PhotoComment[];
  getFilteredAlbums: () => Album[];

  // Utility
  /**
   * Load albums, photos and comments from Supabase. Idempotent — guarded by
   * `isLoaded` / `isLoading`.
   */
  load: () => Promise<void>;
  /**
   * Force a fresh read from the backend, bypassing the `isLoaded` /
   * `isLoading` guards, and replace the store data with the result. Used for
   * catch-up after a realtime reconnect and on window focus/online events.
   * Never toggles `isLoading` (no skeleton flashes).
   */
  refetch: () => Promise<void>;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const initialFilters: AlbumFilters = {
  showArchived: false,
};

const getInitialState = () => ({
  // The store starts empty and is hydrated by `load()` from Supabase.
  albums: [] as Album[],
  photos: [] as Photo[],
  comments: [] as PhotoComment[],
  selectedAlbumId: null as string | null,
  selectedPhotoId: null as string | null,
  isLoading: false,
  error: null as string | null,
  isLoaded: false,
  filters: initialFilters,
  sortBy: 'updatedAt' as AlbumSortBy,
  sortDirection: 'desc' as SortDirection,
  viewMode: 'grid' as const,
  isViewerOpen: false,
  isCreateModalOpen: false,
  editingAlbumId: null as string | null,
});

// ============================================
// Helper Functions
// ============================================

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function isoNow(): string {
  return new Date().toISOString();
}

// Concurrency guard for background refetches (module-level — not rendered
// state, so it never causes loading-skeleton flashes).
let refetchInFlight = false;

/**
 * Persist a store mutation to the backend. Fire-and-forget with error
 * surfacing: on failure the optional `rollback` reverts the optimistic state
 * (no ghost data), the failure lands in `state.error` and the user gets a
 * destructive toast.
 */
function persistAlbumMutation(
  fallbackMessage: string,
  run: () => Promise<void>,
  rollback?: () => void
): void {
  void run().catch((error: unknown) => {
    rollback?.();
    const message = error instanceof Error ? error.message : fallbackMessage;
    useAlbumStore.setState({ error: message });
    toast({
      variant: ToastVariant.Destructive,
      title: 'Грешка при запазване',
      description: message,
    });
  });
}

/** Swap an optimistic album for its persisted counterpart. */
function reconcileAlbum(optimisticId: string, persisted: Album): void {
  useAlbumStore.setState((state) => ({
    albums: state.albums
      .filter((a) => a.id !== persisted.id || a.id === optimisticId)
      .map((a) =>
        a.id === optimisticId ? { ...persisted, photoCount: a.photoCount } : a
      ),
    photos: state.photos.map((p) =>
      p.albumId === optimisticId ? { ...p, albumId: persisted.id } : p
    ),
    selectedAlbumId:
      state.selectedAlbumId === optimisticId
        ? persisted.id
        : state.selectedAlbumId,
    editingAlbumId:
      state.editingAlbumId === optimisticId
        ? persisted.id
        : state.editingAlbumId,
  }));
}

/** Swap an optimistic photo for its persisted counterpart. */
function reconcilePhoto(optimisticId: string, persisted: Photo): void {
  useAlbumStore.setState((state) => ({
    photos: state.photos
      .filter((p) => p.id !== persisted.id || p.id === optimisticId)
      .map((p) => (p.id === optimisticId ? persisted : p)),
    comments: state.comments.map((c) =>
      c.photoId === optimisticId ? { ...c, photoId: persisted.id } : c
    ),
    selectedPhotoId:
      state.selectedPhotoId === optimisticId
        ? persisted.id
        : state.selectedPhotoId,
  }));
}

/** Swap an optimistic comment for its persisted counterpart. */
function reconcilePhotoComment(
  optimisticId: string,
  persisted: PhotoComment
): void {
  useAlbumStore.setState((state) => ({
    comments: state.comments
      .filter((c) => c.id !== persisted.id || c.id === optimisticId)
      .map((c) => (c.id === optimisticId ? persisted : c)),
  }));
}

// ============================================
// Store
// ============================================

export const useAlbumStore = create<AlbumState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Album Actions
  // ============================================

  addAlbum: (input: CreateAlbumInput, createdBy: string) => {
    const now = isoNow();

    // THE EMOJI IS DERIVED FROM THE CATEGORY, exactly as `rowToAlbum` derives
    // it in ../services/supabaseAlbumService.ts. This used to spread
    // `input.emoji` instead, which the service no longer reads at all: there is
    // no `albums.emoji` column, so whatever the caller passed was displayed
    // optimistically and then silently replaced by the category's own emoji the
    // moment `reconcileAlbum` swapped in the persisted album. Deriving it here
    // makes the optimistic album byte-identical to the one that comes back, so
    // reconciliation is invisible instead of a flicker — and it removes the
    // last consumer of a field that goes nowhere.
    const newAlbum: Album = {
      id: generateId('album'),
      title: input.title,
      category: input.category,
      emoji: ALBUM_CATEGORY_CONFIG[input.category].emoji,
      visibility: input.visibility ?? DEFAULT_ALBUM_SETTINGS.visibility,
      memberIds: input.memberIds ?? [],
      createdBy,
      photoCount: 0,
      isArchived: false,
      createdAt: now,
      updatedAt: now,
      ...(input.description !== undefined && {
        description: input.description,
      }),
    };

    set((state) => ({
      albums: [...state.albums, newAlbum],
      isCreateModalOpen: false,
    }));

    persistAlbumMutation(
      'Албумът не можа да бъде запазен.',
      async () => {
        const persisted = await albumService.createAlbum(input, createdBy);
        reconcileAlbum(newAlbum.id, persisted);
      },
      () => {
        useAlbumStore.setState((state) => ({
          albums: state.albums.filter((a) => a.id !== newAlbum.id),
          selectedAlbumId:
            state.selectedAlbumId === newAlbum.id
              ? null
              : state.selectedAlbumId,
        }));
      }
    );

    return newAlbum;
  },

  updateAlbum: (albumId: string, updates: Partial<UpdateAlbumInput>) => {
    const previousAlbum = get().albums.find((a) => a.id === albumId);

    set((state) => ({
      albums: state.albums.map((album): Album => {
        if (album.id !== albumId) return album;

        // The same derivation as `addAlbum`, for the same reason, and here it
        // also fixes a stale icon: `updates.emoji` is ignored by
        // `supabaseAlbumService.updateAlbum` (no column to write it to), and
        // this store discards the album that call returns, so anything shown
        // from `updates.emoji` would survive until the next load and then
        // change on its own. Deriving from the category — the one that was just
        // written, or the album's existing one when the edit does not touch it
        // — is what `rowToAlbum` will do on that load.
        return {
          ...album,
          ...updates,
          emoji:
            ALBUM_CATEGORY_CONFIG[updates.category ?? album.category].emoji,
          updatedAt: isoNow(),
        } as Album;
      }),
      editingAlbumId: null,
    }));

    if (isPersistedId(albumId)) {
      persistAlbumMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await albumService.updateAlbum(albumId, updates);
        },
        () => {
          if (!previousAlbum) return;
          useAlbumStore.setState((state) => ({
            albums: state.albums.map((a) =>
              a.id === albumId ? previousAlbum : a
            ),
          }));
        }
      );
    }
  },

  deleteAlbum: (albumId: string) => {
    const deletedAlbum = get().albums.find((a) => a.id === albumId);
    const deletedPhotos = get().photos.filter((p) => p.albumId === albumId);
    const deletedPhotoIds = deletedPhotos.map((p) => p.id);
    const deletedComments = get().comments.filter((c) =>
      deletedPhotoIds.includes(c.photoId)
    );

    set((state) => {
      // Get all photo IDs in this album
      const photoIds = state.photos
        .filter((p) => p.albumId === albumId)
        .map((p) => p.id);

      return {
        albums: state.albums.filter((a) => a.id !== albumId),
        photos: state.photos.filter((p) => p.albumId !== albumId),
        comments: state.comments.filter((c) => !photoIds.includes(c.photoId)),
        selectedAlbumId:
          state.selectedAlbumId === albumId ? null : state.selectedAlbumId,
      };
    });

    if (isPersistedId(albumId)) {
      persistAlbumMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await albumService.deleteAlbum(albumId);
        },
        () => {
          if (!deletedAlbum) return;
          useAlbumStore.setState((state) => ({
            albums: state.albums.some((a) => a.id === albumId)
              ? state.albums
              : [...state.albums, deletedAlbum],
            photos: [
              ...state.photos,
              ...deletedPhotos.filter(
                (dp) => !state.photos.some((p) => p.id === dp.id)
              ),
            ],
            comments: [
              ...state.comments,
              ...deletedComments.filter(
                (dc) => !state.comments.some((c) => c.id === dc.id)
              ),
            ],
          }));
        }
      );
    }
  },

  archiveAlbum: (albumId: string) => {
    const previousAlbum = get().albums.find((a) => a.id === albumId);

    set((state) => ({
      albums: state.albums.map(
        (album): Album =>
          album.id === albumId
            ? { ...album, isArchived: true, updatedAt: isoNow() }
            : album
      ),
    }));

    if (isPersistedId(albumId)) {
      persistAlbumMutation(
        'Архивирането не можа да бъде запазено.',
        async () => {
          await albumService.archiveAlbum(albumId);
        },
        () => {
          if (!previousAlbum) return;
          useAlbumStore.setState((state) => ({
            albums: state.albums.map((a) =>
              a.id === albumId ? previousAlbum : a
            ),
          }));
        }
      );
    }
  },

  restoreAlbum: (albumId: string) => {
    const previousAlbum = get().albums.find((a) => a.id === albumId);

    set((state) => ({
      albums: state.albums.map(
        (album): Album =>
          album.id === albumId
            ? { ...album, isArchived: false, updatedAt: isoNow() }
            : album
      ),
    }));

    if (isPersistedId(albumId)) {
      persistAlbumMutation(
        'Възстановяването не можа да бъде запазено.',
        async () => {
          await albumService.restoreAlbum(albumId);
        },
        () => {
          if (!previousAlbum) return;
          useAlbumStore.setState((state) => ({
            albums: state.albums.map((a) =>
              a.id === albumId ? previousAlbum : a
            ),
          }));
        }
      );
    }
  },

  // ============================================
  // Photo Actions
  // ============================================

  addPhoto: (input: AddPhotoInput, uploadedBy: string) => {
    const now = isoNow();

    // `input.url` is a RAW STORAGE KEY since migration 020 — the `photos`
    // bucket is private and the column holds a key, not a URL. It goes into
    // `storagePath`, never into `url`: a raw key in an `<img src>` is a
    // relative request against the app's own origin and a broken image.
    //
    // What renders in the meantime is `input.previewUrl`, the signature
    // PhotoUploader minted for the object it just uploaded. It covers the one
    // round-trip between this optimistic insert and `reconcilePhoto` swapping
    // in the persisted, service-signed row. If it is absent (the offline path)
    // the URLs stay empty and the render sites show their placeholder, which is
    // the honest thing to show for a photo that is not yet readable.
    const previewUrl = input.previewUrl ?? '';

    const newPhoto: Photo = {
      id: generateId('photo'),
      albumId: input.albumId,
      storagePath: input.url,
      url: previewUrl,
      visibility: input.visibility ?? DEFAULT_PHOTO_SETTINGS.visibility,
      isCover: DEFAULT_PHOTO_SETTINGS.isCover,
      uploadedBy,
      uploadedAt: now,
      updatedAt: now,
      ...(input.caption !== undefined && { caption: input.caption }),
      ...(input.tags !== undefined && { tags: input.tags }),
      ...(input.taggedMembers !== undefined && {
        taggedMembers: input.taggedMembers,
      }),
      ...(input.takenAt !== undefined && { takenAt: input.takenAt }),
      ...(input.metadata !== undefined && { metadata: input.metadata }),
    };

    set((state) => {
      // Update album photo count
      const updatedAlbums = state.albums.map(
        (album): Album =>
          album.id === input.albumId
            ? {
                ...album,
                photoCount: album.photoCount + 1,
                updatedAt: now,
              }
            : album
      );

      return {
        photos: [...state.photos, newPhoto],
        albums: updatedAlbums,
      };
    });

    // Write-through: persist the photo ROW (the binary upload happens in
    // PhotoUploader before this action runs) and reconcile the id.
    // On failure the optimistic photo and photoCount bump are reverted.
    persistAlbumMutation(
      'Снимката не можа да бъде запазена.',
      async () => {
        const persisted = await albumService.addPhoto(input, uploadedBy);
        reconcilePhoto(newPhoto.id, persisted);
      },
      () => {
        useAlbumStore.setState((state) => {
          if (!state.photos.some((p) => p.id === newPhoto.id)) return {};
          return {
            photos: state.photos.filter((p) => p.id !== newPhoto.id),
            albums: state.albums.map(
              (album): Album =>
                album.id === input.albumId
                  ? {
                      ...album,
                      photoCount: Math.max(0, album.photoCount - 1),
                    }
                  : album
            ),
            selectedPhotoId:
              state.selectedPhotoId === newPhoto.id
                ? null
                : state.selectedPhotoId,
          };
        });
      }
    );

    return newPhoto;
  },

  updatePhoto: (photoId: string, updates: Partial<UpdatePhotoInput>) => {
    const previousPhoto = get().photos.find((p) => p.id === photoId);

    set((state) => ({
      photos: state.photos.map((photo): Photo => {
        if (photo.id !== photoId) return photo;

        return {
          ...photo,
          ...updates,
          updatedAt: isoNow(),
        };
      }),
    }));

    if (isPersistedId(photoId)) {
      persistAlbumMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await albumService.updatePhoto(photoId, updates);
        },
        () => {
          if (!previousPhoto) return;
          useAlbumStore.setState((state) => ({
            photos: state.photos.map((p) =>
              p.id === photoId ? previousPhoto : p
            ),
          }));
        }
      );
    }
  },

  deletePhoto: (photoId: string) => {
    const deletedPhoto = get().photos.find((p) => p.id === photoId);
    const deletedPhotoComments = get().comments.filter(
      (c) => c.photoId === photoId
    );
    const previousAlbum = deletedPhoto
      ? get().albums.find((a) => a.id === deletedPhoto.albumId)
      : undefined;

    set((state) => {
      const photo = state.photos.find((p) => p.id === photoId);
      if (!photo) return state;

      // Update album photo count
      const updatedAlbums = state.albums.map((album): Album => {
        if (album.id !== photo.albumId) return album;

        const baseUpdate = {
          ...album,
          photoCount: Math.max(0, album.photoCount - 1),
          updatedAt: isoNow(),
        };

        // Clear cover if this was the cover photo
        if (album.coverPhotoId === photoId) {
          const { coverPhotoId: _, coverUrl: __, ...rest } = baseUpdate;
          return rest;
        }

        return baseUpdate;
      });

      return {
        photos: state.photos.filter((p) => p.id !== photoId),
        comments: state.comments.filter((c) => c.photoId !== photoId),
        albums: updatedAlbums,
        selectedPhotoId:
          state.selectedPhotoId === photoId ? null : state.selectedPhotoId,
      };
    });

    if (isPersistedId(photoId)) {
      persistAlbumMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await albumService.deletePhoto(photoId);
        },
        () => {
          if (!deletedPhoto) return;
          useAlbumStore.setState((state) => ({
            photos: state.photos.some((p) => p.id === photoId)
              ? state.photos
              : [...state.photos, deletedPhoto],
            comments: [
              ...state.comments,
              ...deletedPhotoComments.filter(
                (dc) => !state.comments.some((c) => c.id === dc.id)
              ),
            ],
            albums: previousAlbum
              ? state.albums.map((a) =>
                  a.id === previousAlbum.id ? previousAlbum : a
                )
              : state.albums,
          }));
        }
      );
    }
  },

  setAlbumCover: (albumId: string, photoId: string) => {
    const photo = get().photos.find((p) => p.id === photoId);
    if (!photo) return;
    const previousAlbum = get().albums.find((a) => a.id === albumId);
    const previousPhotos = get().photos.filter((p) => p.albumId === albumId);

    set((state) => ({
      albums: state.albums.map(
        (album): Album =>
          album.id === albumId
            ? {
                ...album,
                coverPhotoId: photoId,
                // `photo.url` is a SIGNED URL, which is exactly what `coverUrl`
                // means, so copying it across is still right. What changed is
                // that it can now be `''` — a photo whose key would not sign.
                // Only set the field when there is something renderable, so
                // `album.coverUrl ? … : placeholder` stays the one check
                // AlbumCard has to make, and so an optimistic cover never
                // overwrites a good URL with an empty one.
                ...(photo.url !== '' && { coverUrl: photo.url }),
                updatedAt: isoNow(),
              }
            : album
      ),
      photos: state.photos.map(
        (p): Photo => ({
          ...p,
          isCover: p.albumId === albumId ? p.id === photoId : p.isCover,
        })
      ),
    }));

    if (isPersistedId(albumId) && isPersistedId(photoId)) {
      persistAlbumMutation(
        'Корицата не можа да бъде запазена.',
        async () => {
          await albumService.setAlbumCover(albumId, photoId);
        },
        () => {
          useAlbumStore.setState((state) => ({
            albums: previousAlbum
              ? state.albums.map((a) => (a.id === albumId ? previousAlbum : a))
              : state.albums,
            photos: state.photos.map((p) => {
              const previous = previousPhotos.find((pp) => pp.id === p.id);
              return previous ?? p;
            }),
          }));
        }
      );
    }
  },

  // ============================================
  // Comment Actions
  // ============================================

  addComment: (photoId: string, text: string, authorId: string) => {
    const newComment: PhotoComment = {
      id: generateId('comment'),
      photoId,
      text,
      authorId,
      createdAt: isoNow(),
    };

    set((state) => ({
      comments: [...state.comments, newComment],
    }));

    if (isPersistedId(photoId)) {
      persistAlbumMutation(
        'Коментарът не можа да бъде запазен.',
        async () => {
          const persisted = await albumService.addComment(
            photoId,
            text,
            authorId
          );
          reconcilePhotoComment(newComment.id, persisted);
        },
        () => {
          useAlbumStore.setState((state) => ({
            comments: state.comments.filter((c) => c.id !== newComment.id),
          }));
        }
      );
    }

    return newComment;
  },

  deleteComment: (commentId: string) => {
    const deletedComment = get().comments.find((c) => c.id === commentId);

    set((state) => ({
      comments: state.comments.filter((c) => c.id !== commentId),
    }));

    if (isPersistedId(commentId)) {
      persistAlbumMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await albumService.deleteComment(commentId);
        },
        () => {
          if (!deletedComment) return;
          useAlbumStore.setState((state) =>
            state.comments.some((c) => c.id === commentId)
              ? {}
              : { comments: [...state.comments, deletedComment] }
          );
        }
      );
    }
  },

  // ============================================
  // Realtime Actions (P7)
  // ============================================

  upsertRealtimeAlbum: (album: Album) => {
    set((state) => {
      const existing = state.albums.find((a) => a.id === album.id);
      if (existing) {
        // Drop stale echoes (e.g. our own optimistic write coming back).
        if (existing.updatedAt >= album.updatedAt) return {};
        // Preserve the locally-tracked photoCount maintained by photo upserts.
        const merged: Album = { ...album, photoCount: existing.photoCount };
        return {
          albums: state.albums.map((a) => (a.id === album.id ? merged : a)),
        };
      }
      return { albums: [...state.albums, album] };
    });
  },

  deleteRealtimeAlbum: (albumId: string) => {
    set((state) => {
      const photoIds = state.photos
        .filter((p) => p.albumId === albumId)
        .map((p) => p.id);

      return {
        albums: state.albums.filter((a) => a.id !== albumId),
        photos: state.photos.filter((p) => p.albumId !== albumId),
        comments: state.comments.filter((c) => !photoIds.includes(c.photoId)),
        selectedAlbumId:
          state.selectedAlbumId === albumId ? null : state.selectedAlbumId,
      };
    });
  },

  upsertRealtimePhoto: (photo: Photo) => {
    set((state) => {
      const existing = state.photos.find((p) => p.id === photo.id);
      if (existing) {
        // Drop stale echoes (e.g. our own optimistic write coming back).
        if (existing.updatedAt >= photo.updatedAt) return {};
        return {
          photos: state.photos.map((p) => (p.id === photo.id ? photo : p)),
        };
      }

      // New photo: append and bump the parent album's count.
      const albums = state.albums.map(
        (album): Album =>
          album.id === photo.albumId
            ? { ...album, photoCount: album.photoCount + 1 }
            : album
      );
      return { photos: [...state.photos, photo], albums };
    });
  },

  deleteRealtimePhoto: (photoId: string) => {
    set((state) => {
      const photo = state.photos.find((p) => p.id === photoId);
      if (!photo) return {};

      const albums = state.albums.map((album): Album => {
        if (album.id !== photo.albumId) return album;

        const baseUpdate = {
          ...album,
          photoCount: Math.max(0, album.photoCount - 1),
        };

        // Clear cover if this was the cover photo.
        if (album.coverPhotoId === photoId) {
          const { coverPhotoId: _, coverUrl: __, ...rest } = baseUpdate;
          return rest;
        }

        return baseUpdate;
      });

      return {
        photos: state.photos.filter((p) => p.id !== photoId),
        comments: state.comments.filter((c) => c.photoId !== photoId),
        albums,
        selectedPhotoId:
          state.selectedPhotoId === photoId ? null : state.selectedPhotoId,
      };
    });
  },

  upsertRealtimeComment: (comment: PhotoComment) => {
    set((state) => {
      const exists = state.comments.some((c) => c.id === comment.id);
      return {
        comments: exists
          ? state.comments.map((c) => (c.id === comment.id ? comment : c))
          : [...state.comments, comment],
      };
    });
  },

  deleteRealtimeComment: (commentId: string) => {
    set((state) => ({
      comments: state.comments.filter((c) => c.id !== commentId),
    }));
  },

  // ============================================
  // Selection Actions
  // ============================================

  setSelectedAlbumId: (albumId: string | null) => {
    set({ selectedAlbumId: albumId, selectedPhotoId: null });
  },

  setSelectedPhotoId: (photoId: string | null) => {
    set({ selectedPhotoId: photoId });
  },

  // ============================================
  // Filter & Sort Actions
  // ============================================

  setFilters: (filters: Partial<AlbumFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  clearFilters: () => {
    set({ filters: initialFilters });
  },

  setSortBy: (sortBy: AlbumSortBy) => {
    set({ sortBy });
  },

  setSortDirection: (direction: SortDirection) => {
    set({ sortDirection: direction });
  },

  // ============================================
  // View Actions
  // ============================================

  setViewMode: (mode: 'grid' | 'list') => {
    set({ viewMode: mode });
  },

  openViewer: (photoId: string) => {
    set({ selectedPhotoId: photoId, isViewerOpen: true });
  },

  closeViewer: () => {
    set({ isViewerOpen: false });
  },

  // ============================================
  // Modal Actions
  // ============================================

  openCreateModal: () => {
    set({ isCreateModalOpen: true, editingAlbumId: null });
  },

  closeCreateModal: () => {
    set({ isCreateModalOpen: false, editingAlbumId: null });
  },

  openEditModal: (albumId: string) => {
    set({ editingAlbumId: albumId, isCreateModalOpen: true });
  },

  // ============================================
  // Getters
  // ============================================

  getAlbumById: (albumId: string) => {
    return get().albums.find((a) => a.id === albumId);
  },

  getPhotosByAlbumId: (albumId: string) => {
    return get().photos.filter((p) => p.albumId === albumId);
  },

  getCommentsByPhotoId: (photoId: string) => {
    return get().comments.filter((c) => c.photoId === photoId);
  },

  getFilteredAlbums: () => {
    const { albums, filters, sortBy, sortDirection } = get();

    let filtered = [...albums];

    // Apply filters
    if (!filters.showArchived) {
      filtered = filtered.filter((a) => !a.isArchived);
    }

    if (filters.category) {
      filtered = filtered.filter((a) => a.category === filters.category);
    }

    if (filters.visibility) {
      filtered = filtered.filter((a) => a.visibility === filters.visibility);
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(search) ||
          a.description?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title, 'bg');
          break;
        case 'createdAt':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          comparison =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case 'photoCount':
          comparison = a.photoCount - b.photoCount;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  },

  // ============================================
  // Utility
  // ============================================

  load: async () => {
    const { isLoaded, isLoading } = get();
    if (isLoaded || isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const [albums, photos, comments] = await Promise.all([
        albumService.getAlbums(),
        albumService.getAllPhotos(),
        albumService.getAllComments(),
      ]);
      set({
        albums: [...albums],
        photos: [...photos],
        comments: [...comments],
        isLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на албумите',
        isLoading: false,
      });
    }
  },

  refetch: async () => {
    if (refetchInFlight) return;
    refetchInFlight = true;
    try {
      const [albums, photos, comments] = await Promise.all([
        albumService.getAlbums(),
        albumService.getAllPhotos(),
        albumService.getAllComments(),
      ]);
      set({
        albums: [...albums],
        photos: [...photos],
        comments: [...comments],
        isLoaded: true,
        error: null,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на албумите',
      });
    } finally {
      refetchInFlight = false;
    }
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks (for performance)
// ============================================

/**
 * Select albums with current filters applied
 * Uses useMemo pattern outside of Zustand to avoid infinite loops
 */
export const useFilteredAlbums = () => {
  const albums = useAlbumStore((state) => state.albums);
  const filters = useAlbumStore((state) => state.filters);
  const sortBy = useAlbumStore((state) => state.sortBy);
  const sortDirection = useAlbumStore((state) => state.sortDirection);

  // Compute filtered albums outside of the selector
  let filtered = [...albums];

  // Apply filters
  if (!filters.showArchived) {
    filtered = filtered.filter((a) => !a.isArchived);
  }

  if (filters.category) {
    filtered = filtered.filter((a) => a.category === filters.category);
  }

  if (filters.visibility) {
    filtered = filtered.filter((a) => a.visibility === filters.visibility);
  }

  if (filters.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(
      (a) =>
        a.title.toLowerCase().includes(search) ||
        a.description?.toLowerCase().includes(search)
    );
  }

  // Apply sorting
  filtered.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title, 'bg');
        break;
      case 'createdAt':
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'updatedAt':
        comparison =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case 'photoCount':
        comparison = a.photoCount - b.photoCount;
        break;
      default:
        comparison = 0;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return filtered;
};

/**
 * Select currently selected album
 */
export const useSelectedAlbum = () => {
  const selectedAlbumId = useAlbumStore((state) => state.selectedAlbumId);
  const albums = useAlbumStore((state) => state.albums);

  if (!selectedAlbumId) return null;
  return albums.find((a) => a.id === selectedAlbumId) ?? null;
};

/**
 * Select photos for the currently selected album
 */
export const useSelectedAlbumPhotos = () => {
  const selectedAlbumId = useAlbumStore((state) => state.selectedAlbumId);
  const photos = useAlbumStore((state) => state.photos);

  if (!selectedAlbumId) return [];
  return photos.filter((p) => p.albumId === selectedAlbumId);
};

/**
 * Select currently selected photo
 */
export const useSelectedPhoto = () => {
  const selectedPhotoId = useAlbumStore((state) => state.selectedPhotoId);
  const photos = useAlbumStore((state) => state.photos);

  if (!selectedPhotoId) return null;
  return photos.find((p) => p.id === selectedPhotoId) ?? null;
};

/**
 * Select comments for the currently selected photo
 */
export const useSelectedPhotoComments = () => {
  const selectedPhotoId = useAlbumStore((state) => state.selectedPhotoId);
  const comments = useAlbumStore((state) => state.comments);

  if (!selectedPhotoId) return [];
  return comments.filter((c) => c.photoId === selectedPhotoId);
};

// Clear album data whenever the active family changes so the next family
// never sees the previous scope's albums/photos.
registerFamilyScopedReset(() => {
  useAlbumStore.getState().reset();
});
